import React, { Component } from 'react';
import queryString from 'query-string';

import ManagedComponent from '../components/managed-component';

import Webconfig from './webconfig';
import { SectionChild } from '../components/section';

const OAUTH_DATA_KEY = '__oauth';
const FITBIT_URL = 'https://api.fitbit.com';

const urlSafeBase64 = (string) =>
  btoa(string)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const createRandomString = (size = 32) => {
  const buffer = window.crypto.getRandomValues(new Uint8Array(size));

  let randomString = '';
  for (const code of buffer) {
    randomString += String.fromCharCode(code);
  }

  // URL Safe base64 string
  return urlSafeBase64(randomString);
};

class OAuth extends Component {
  static _type = 'Oauth';
  _type = 'Oauth';

  static defaultProps = {
    pkce: false,
    encodeAsBase64: false,
    onAccessToken: () => {}
  };

  setOauthData(updatedOauthData) {
    const {
      oauthData,
      onOauthDataChange
    } = this.props;

    onOauthDataChange({
      ...oauthData,
      ...updatedOauthData
    });
  }

  resetOauthData() {
    this.props.onOauthDataChange({});
  }

  constructUrl = (redirectUrl, callbackUrl) => {
    const {
      url,
      authorizeUrl = url,
      clientId,
      scope,
      oAuthParams,
      pkce,
      encodeAsBase64
    } = this.props;

    const nonce = createRandomString(128);
    const codeVerifier = createRandomString();

    this.setOauthData({
      redirectUrl,
      codeVerifier,
      nonce
    });

    const state = JSON.stringify({
      callbackUrl,
      nonce
    });

    let queryParams = {
      redirect_uri: redirectUrl,
      state: encodeAsBase64 ? `b64${urlSafeBase64(state)}` : state,
      client_id: clientId,
      response_type: 'code',
      scope,
      ...oAuthParams
    };

    if (pkce) {
      queryParams = {
        ...queryParams,
        // FIXME replace with S256 when using security approved code generation
        code_challenge_method: 'plain',
        code_challenge: codeVerifier
      };
    }

    return `${authorizeUrl}?${queryString.stringify(queryParams)}`;
  };

  isValidReturn(params) {
    const { state } = params;

    if (!state) {
      return false;
    }

    const { nonce: savedNonce } = this.props.oauthData;
    try {
      const { nonce: stateNonce } = JSON.parse(decodeURIComponent(state));

      return savedNonce === stateNonce;
    } catch(err) {
      return false;
    }
  }

  onReturn = async (data) => {
    const oauthCode = queryString.parse(data);

    // Short circuit because the developer wants custom logic
    if (typeof this.props.onReturn === 'function') {
      this.props.onReturn(oauthCode);
      return;
    }

    const {
      requestTokenUrl,
      clientId,
      clientSecret,
      pkce,
      oauthData
    } = this.props;

    const {
      redirectUrl,
      codeVerifier
    } = oauthData;

    // Ignore returns that are invalid or unknown
    if (!this.isValidReturn(oauthCode)) {
      return;
    }

    const body = new URLSearchParams();
    body.append('client_id', clientId);
    body.append('client_secret', clientSecret);
    body.append('code', oauthCode.code);
    body.append('grant_type', 'authorization_code');
    body.append('redirect_uri', redirectUrl);

    if (pkce) {
      body.append('code_verifier', codeVerifier);
    }

    // Fitbit requires authorization header to request access token
    const headers = new Headers();
    if (requestTokenUrl.includes(FITBIT_URL)) {
      headers.append('Authorization', `Basic ${btoa(`${clientId}:${clientSecret}`)}`);
    }

    const response = await fetch(requestTokenUrl, {
      method: 'POST',
      headers,
      body
    });

    const oauthReturnData = await response.json();

    this.props.onAccessToken(oauthReturnData);
    this.resetOauthData();
  }

  render() {
    const {
      label = 'Login',
      status = 'Login',
      disabled,
      showError,
      title,
      description
    } = this.props;

    return (
      <SectionChild title={title} description={description}>
        <Webconfig
          title={title}
          label={label}
          status={status}
          disabled={disabled}
          showError={showError}
          description={description}
          constructUrl={this.constructUrl}
          onReturn={this.onReturn}
        />
      </SectionChild>
    );
  }
}

export default class extends Component {
  parseValue(rawValue) {
    try {
      return JSON.parse(rawValue);
    } catch (err) {
      return {};
    }
  }

  renderOauth(onAccessToken) {
    const {
      label,
      status,
      disabled,
      showError,
      title,
      description,
      url,
      authorizeUrl = url,
      requestTokenUrl,
      clientId,
      clientSecret,
      scope,
      oAuthParams,
      pkce,
      encodeAsBase64,
      onReturn
    } = this.props;

    const oauthProps = {
      label,
      status,
      disabled,
      showError,
      title,
      description,
      url,
      authorizeUrl,
      requestTokenUrl,
      clientId,
      clientSecret,
      scope,
      oAuthParams,
      pkce,
      encodeAsBase64,
      onReturn,
      onAccessToken
    };

    return (
      <ManagedComponent settingsKey={OAUTH_DATA_KEY}>
        {({ value, onChange }) => (
          <OAuth
            {...oauthProps}
            oauthData={this.parseValue(value)}
            onOauthDataChange={
              (oauthData) => onChange(JSON.stringify(oauthData))}
          />
        )}
      </ManagedComponent>
    );
  }

  render() {
    const {
      onReturn,
      onAccessToken,
      settingsKey
    } = this.props;

    if (!settingsKey || onReturn) {
      return this.renderOauth(onAccessToken);
    }

    return (
      <ManagedComponent settingsKey={settingsKey}>
        {({ onChange }) => (
          this.renderOauth((oauthReturnData) => {
            if (typeof onAccessToken === 'function') {
              onAccessToken(oauthReturnData);
            }

            onChange(JSON.stringify(oauthReturnData));
          })
        )}
      </ManagedComponent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/fitbit-components/oauth.js